import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ConfirmProvider } from '@/components/common/ConfirmDialog';
import { PromptProvider } from '@/components/common/PromptDialog';
import { LoginPage } from '@/modules/auth/pages/LoginPage';
import { SelectEmpresaPage } from '@/modules/auth/pages/SelectEmpresaPage';
import { AppLayout } from '@/modules/layout/components/AppLayout';
import { ProtectedRoute } from '@/modules/layout/components/ProtectedRoute';
import { RootRedirect } from '@/modules/layout/components/RootRedirect';
import { DashboardPage } from '@/modules/dashboard/pages/DashboardPage';

// Code-split: las páginas de los módulos pesados se cargan on-demand.
// El initial bundle baja ~50% al sacar del path crítico jspdf, html2canvas,
// xlsx y jszip — que sólo se usan en facturación, finanzas y reportes.
// Auth + Layout + Dashboard quedan eager para que el primer paint sea instantáneo.
const AdministracionesPage = lazy(() =>
  import('@/modules/clientes/pages/AdministracionesPage').then((m) => ({
    default: m.AdministracionesPage,
  })),
);
const EdificiosPage = lazy(() =>
  import('@/modules/clientes/pages/EdificiosPage').then((m) => ({
    default: m.EdificiosPage,
  })),
);
const ControlAbonosPage = lazy(() =>
  import('@/modules/control-abonos/pages/ControlAbonosPage').then((m) => ({
    default: m.ControlAbonosPage,
  })),
);
const FinanzasPage = lazy(() =>
  import('@/modules/finanzas/pages/FinanzasPage').then((m) => ({
    default: m.FinanzasPage,
  })),
);
const FacturacionPage = lazy(() =>
  import('@/modules/facturacion/pages/FacturacionPage').then((m) => ({
    default: m.FacturacionPage,
  })),
);
const RecuperoPage = lazy(() => import('@/modules/recupero/pages/RecuperoPage'));
const ReportesPage = lazy(() =>
  import('@/modules/reportes/pages/ReportesPage').then((m) => ({
    default: m.ReportesPage,
  })),
);
const UsuariosPage = lazy(() =>
  import('@/modules/usuarios/pages/UsuariosPage').then((m) => ({
    default: m.UsuariosPage,
  })),
);
const ConfiguracionPage = lazy(() =>
  import('@/modules/configuracion/pages/ConfiguracionPage').then((m) => ({
    default: m.ConfiguracionPage,
  })),
);
const MiPerfilPage = lazy(() =>
  import('@/modules/perfil/pages/MiPerfilPage').then((m) => ({
    default: m.MiPerfilPage,
  })),
);

/**
 * Loader minimalista mientras llega el chunk. Tiene que ser muy chico
 * porque vive en el bundle inicial.
 */
function PageFallback() {
  return (
    <div
      className="flex h-full min-h-[40vh] w-full items-center justify-center text-sm text-neutral-muted"
      role="status"
      aria-busy="true"
    >
      <span className="animate-pulse">Cargando…</span>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <PromptProvider>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/select-empresa"
              element={
                <ProtectedRoute allowRoles={['apex']}>
                  <SelectEmpresaPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/e/:empresaId"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<DashboardPage />} />
              <Route
                path="clientes/administraciones"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <AdministracionesPage />
                  </Suspense>
                }
              />
              <Route
                path="clientes/edificios"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <EdificiosPage />
                  </Suspense>
                }
              />
              <Route
                path="control-abonos"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <ControlAbonosPage />
                  </Suspense>
                }
              />
              <Route
                path="finanzas"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <FinanzasPage />
                  </Suspense>
                }
              />
              <Route
                path="facturacion"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <FacturacionPage />
                  </Suspense>
                }
              />
              <Route
                path="recupero"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <RecuperoPage />
                  </Suspense>
                }
              />
              <Route path="conciliacion" element={<ConciliacionRedirect />} />
              <Route
                path="reportes"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <ReportesPage />
                  </Suspense>
                }
              />
              <Route
                path="usuarios"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <UsuariosPage />
                  </Suspense>
                }
              />
              <Route
                path="mi-perfil"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <MiPerfilPage />
                  </Suspense>
                }
              />
              <Route
                path="configuracion"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <ConfiguracionPage />
                  </Suspense>
                }
              />
            </Route>
            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </PromptProvider>
      </ConfirmProvider>
    </AuthProvider>
  );
}

function ConciliacionRedirect() {
  const { empresaId } = useParams<{ empresaId: string }>();
  return <Navigate to={`/e/${empresaId}/finanzas?tab=conciliacion`} replace />;
}
